import {TFunction} from 'i18next';

import {JobPosting} from '../../features/company/recruiting/job-postings/types/JobPostingsTypes';
import {
  SUPPORTED_DEGREE_LEVELS,
  SUPPORTED_EMPLOYMENT_TYPES,
  SUPPORTED_LANGUAGE_LEVELS,
  SUPPORTED_WORK_MODES,
} from '../../lib/constants';
import {ALL_LANGUAGES_LIST} from '../../lib/languagesListConst';
import {LanguageProfile} from '../../types/shared-types';
import {SupportedLanguages} from '../../types/SupportedLanguages';

export const formatSalary = (jobPosting: JobPosting): string => {
  if (jobPosting.salary?.min && jobPosting.salary?.max) {
    return `€${jobPosting.salary?.min} - €${jobPosting.salary?.max}`;
  } else if (jobPosting.salary?.min && !jobPosting.salary?.max) {
    return `€${jobPosting.salary?.min}`;
  } else if (!jobPosting.salary?.min && jobPosting.salary?.max) {
    return `€${jobPosting.salary?.max}`;
  } else {
    return '-';
  }
};

export const formatLanguages = (jobPosting: JobPosting, currentLanguage: SupportedLanguages): string => {
  if (jobPosting.languageSkills && jobPosting.languageSkills.length > 0) {
    return jobPosting.languageSkills
      .map((languageSkill) => {
        const language = ALL_LANGUAGES_LIST.find((language) => language.id === languageSkill.language);
        const languageLevel = SUPPORTED_LANGUAGE_LEVELS.find((languageLevel) => languageLevel === languageSkill.level);

        if (language && languageLevel) {
          return `${language.label[currentLanguage]} ${languageLevel}`;
        } else {
          return '';
        }
      })
      .join(', ');
  }
  return '---';
};

export const formatWorkMode = (jobPosting: JobPosting, t: TFunction): string => {
  const workMode = SUPPORTED_WORK_MODES.find((workMode) => workMode.id === jobPosting.workMode);
  return workMode ? t(`shared.workModes.${workMode.id}`) : '---';
};

export const formatEmploymentType = (jobPosting: JobPosting, t: TFunction): string => {
  const employmentTypes = SUPPORTED_EMPLOYMENT_TYPES.filter((type) => jobPosting.employmentType?.includes(type.id)).map(
    (type) => t(`shared.employmentTypes.${type.id}`)
  );

  return employmentTypes.length > 0 ? employmentTypes.join(', ') : '---';
};

export const formatExperienceAndLeaderShip = (
  jobPosting: JobPosting,
  t: TFunction,
  isMultipleExperience: boolean,
  isMultipleLeaderShip: boolean
) => {
  const experienceLabel = isMultipleExperience
    ? 'companyJobPostings.newJobDialog.formLabels.yearsOfExperience'
    : 'companyJobPostings.newJobDialog.formLabels.yearOfExperience';
  const leadershipLabel = isMultipleLeaderShip
    ? 'companyJobPostings.newJobDialog.formLabels.yearsOfLeadership'
    : 'companyJobPostings.newJobDialog.formLabels.yearOfLeadership';

  if (!jobPosting.experience?.yearsOfExperience && !jobPosting.experience?.yearsOfLeadership) {
    return '---';
  } else if (jobPosting.experience?.yearsOfExperience && !jobPosting.experience?.yearsOfLeadership) {
    return `${jobPosting.experience?.yearsOfExperience} ${t(experienceLabel)}`;
  } else if (!jobPosting.experience?.yearsOfExperience && jobPosting.experience?.yearsOfLeadership) {
    return `${jobPosting.experience?.yearsOfLeadership} ${t(leadershipLabel)}`;
  } else {
    return `${jobPosting.experience?.yearsOfExperience} ${t(experienceLabel)}, ${
      jobPosting.experience?.yearsOfLeadership
    } ${t(leadershipLabel)}`;
  }
};

export const formatDegree = (jobPosting: JobPosting, t: TFunction) => {
  const degree = SUPPORTED_DEGREE_LEVELS.find((degree) => degree.id === jobPosting.degree);
  const label =
    t('companyJobPostings.newJobDialog.formLabels.degree').charAt(0).toLowerCase() +
    t('companyJobPostings.newJobDialog.formLabels.degree').slice(1);

  return degree ? `${t(`shared.degrees.${degree.id}`)} ${label}` : '---';
};

export const formatLocations = (jobPosting: JobPosting, currentLanguage: SupportedLanguages) => {
  let jobPostingLocations: string[] = [];

  if (jobPosting.locations && jobPosting.locations.length > 0) {
    jobPostingLocations = jobPosting.locations.map((location, idx) => {
      const defaultCityName = (location.city as LanguageProfile).en;

      if ((location.city as LanguageProfile)[currentLanguage]) {
        return `${(location.city as LanguageProfile)[currentLanguage]}${
          jobPosting.locations && idx < jobPosting.locations?.length - 1 ? ',' : ''
        } `;
      } else {
        return `${defaultCityName}${jobPosting.locations && idx < jobPosting.locations?.length - 1 ? ',' : ''} `;
      }
    });
  }

  if (jobPosting.remoteHiring) {
    if (jobPostingLocations.length > 0) {
      jobPostingLocations[jobPostingLocations.length - 1] = `${jobPostingLocations[
        jobPostingLocations.length - 1
      ].trim()}, `;
    }

    jobPostingLocations.push('Remote');
  }

  return jobPostingLocations;
};
